import { useEffect, useMemo, useState } from 'react';
import {
  composerCommandeFournisseur, decouperAdresses, LIBELLE_LANGUE, REGLAGES_PAR_DEFAUT,
} from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useEnvoiFournisseur } from '../api/hooks';
import type { CommandeListee, DocumentReglages } from '../api/types';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/** Un mail par fournisseur, tel qu'il est relu et corrigé à l'écran. */
interface MailRelu {
  /**
   * Clé d'annuaire de la société, ou `''` tant qu'aucune n'est identifiée.
   *
   * Distincte de `fournisseur`, qui est le LIBELLÉ affiché et vaut alors
   * « (fournisseur à préciser) » : ce texte-là ne doit jamais servir à chercher
   * une adresse, ni partir au serveur comme un nom de société.
   */
  societe: string;
  fournisseur: string;
  /**
   * Les lignes de CE mail, telles qu'elles ont été groupées.
   *
   * Conservées pour pouvoir recomposer en changeant de destinataire sans
   * toucher au contenu. Recomposer depuis `commande.articles` déverserait les
   * lignes des autres fournisseurs dans ce mail-ci, sur une commande qui en
   * compte plusieurs.
   */
  lignes: CommandeListee['articles'];
  destinataire: string;
  objet: string;
  message: string;
  langue: 'fr' | 'en';
  /** Références sans correspondance : elles figurent au mail en « à compléter ». */
  manquants: string[];
  /** `true` quand la commande ne porte aucune ligne. */
  sansProduit: boolean;
  /** `false` quand la société ne figure pas à l'annuaire. */
  contactConnu: boolean;
}

/**
 * Relecture de la commande avant envoi.
 *
 * Elle ne s'ouvre pas à chaque envoi : seulement quand l'application ne peut pas
 * composer seule un mail défendable. Deux situations, et leur remède est le
 * même — montrer le mail et laisser corriger :
 *
 * - **un produit sans correspondance** : il est SORTI du mail plutôt que traduit
 *   par notre désignation interne, que le fournisseur ne reconnaîtrait pas. Les
 *   références écartées sont affichées à côté du texte ;
 * - **un fournisseur absent de l'annuaire** : aucune adresse n'est devinée, le
 *   champ destinataire est à remplir.
 *
 * Ce qui part est exactement ce qui est à l'écran : l'API ne recompose
 * rien, sinon les corrections seraient perdues.
 */
export function CommandeFournisseur({
  commande, reglages, onFermer,
}: {
  commande: CommandeListee;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  const envoi = useEnvoiFournisseur();

  const composes = useMemo(() => composerTous(commande, reglages), [commande, reglages]);
  const [mails, setMails] = useState<MailRelu[]>(composes);
  const [actif, setActif] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => { setMails(composes); setActif(0); }, [composes]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const mail = mails[actif];
  if (!mail) return null;

  function modifier(champ: 'destinataire' | 'objet' | 'message', valeur: string) {
    setMails((precedents) =>
      precedents.map((m, i) => (i === actif ? { ...m, [champ]: valeur } : m)));
  }

  /**
   * Sociétés de l'annuaire qui portent au moins une adresse.
   *
   * Une fiche sans adresse ne pré-remplirait rien : la proposer promettrait un
   * destinataire et laisserait le champ vide.
   */
  const societes = useMemo(() => Object.entries(reglages?.contactsFournisseur ?? {})
    .filter(([, fiche]) => decouperAdresses(fiche?.email ?? '').length)
    .map(([nom]) => nom)
    .sort((a, b) => a.localeCompare(b)), [reglages]);

  /**
   * Désigne le fournisseur d'un mail qu'on n'a pas su rattacher.
   *
   * Le mail est RECOMPOSÉ, pas seulement radressé : la langue, l'objet et le
   * corps suivent la société. Les corrections déjà faites sur ce mail-ci sont
   * donc perdues — c'est voulu, elles n'avaient pas de sens pour un autre
   * destinataire, et le geste se fait avant d'écrire, pas après.
   */
  function designerFournisseur(societe: string) {
    setErreur(null);
    setMails((precedents) => precedents.map(
      (m, i) => (i === actif ? composerUn(commande, reglages, societe, m.lignes) : m),
    ));
  }

  const incomplets = mails.filter((m) => !decouperAdresses(m.destinataire).length);

  /**
   * Le mail affiché n'a aucune adresse exploitable.
   *
   * C'est ce qui commande l'affichage du choix de fournisseur — et non la
   * présence d'une société, qui ne dirait rien d'un champ vidé à la main.
   */
  const sansDestinataire = !decouperAdresses(mail.destinataire).length;

  async function deposer() {
    setErreur(null);
    if (incomplets.length) {
      setErreur(
        `Destinataire manquant : ${incomplets.map((m) => m.fournisseur).join(', ')}. `
        + "Renseignez-le avant d'envoyer.",
      );
      return;
    }

    try {
      const r = await envoi.mutateAsync({
        id: commande.id,
        corps: {
          /*
           * **Repasser dès que la commande a DÉJÀ été transmise.**
           *
           * Le critère était `incident === 'COMMANDE_MANQUANTE'`, et il est
           * devenu trop étroit : le constat « absente du portail », posé par le
           * relevé Fortemix, ne passe pas par `incident`. Une commande comme
           * FAC-20260826-39440 partait donc en « transmettre », et l'API la
           * refusait — « Commande déjà transmise, utilisez repasser ». Le bouton
           * « Repasser la commande » ne pouvait tout simplement pas aboutir.
           *
           * `dateEnvoiFournisseur` est le bon critère parce que c'est
           * exactement celui de l'API : le présentateur le dérive de
           * `envoiFournisseur.envoye`, qui est la condition du refus.
           */
          operation: commande.dateEnvoiFournisseur ? 'repasser' : 'transmettre',
          mails: mails.map(({ fournisseur, destinataire, objet, message }) => ({
            fournisseur, destinataire, objet, message,
          })),
          // Les remboursements connus quand ces mails ont été composés : un
          // remboursement arrivé depuis fait refuser l'envoi, plutôt que de
          // commander un article que le client ne paiera pas.
          ajustementsConnus: commande.shopify?.ids ?? [],
        },
      });

      const echecs = r.brouillons.filter((b) => !b.depose);
      if (echecs.length) {
        setErreur(
          echecs.map((b) => `${b.societe} : ${b.erreur ?? 'raison inconnue'}`).join(' · '),
        );
        return;
      }

      const simule = r.brouillons.some((b) => b.simule);
      ajouter(
        simule
          ? 'Commande composée, mais Outlook est en mode local : aucun mail envoyé.'
          : `Commande envoyée à ${r.brouillons.map((b) => b.societe).join(', ')} depuis contact@.`,
        simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "La commande n'a pas pu être envoyée.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Commande fournisseur ${commande.ref}`}>
        <div className="mhead">
          <h2>Commande à relire — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          {mails.length > 1 && (
            <div className="field">
              <label htmlFor="cf-frs">Fournisseur</label>
              <select id="cf-frs" value={actif} onChange={(e) => setActif(Number(e.target.value))}>
                {mails.map((m, i) => (
                  <option key={m.fournisseur} value={i}>
                    {m.fournisseur}
                    {m.manquants.length ? ` — ${m.manquants.length} produit(s) écarté(s)` : ''}
                    {decouperAdresses(m.destinataire).length ? '' : ' — sans destinataire'}
                  </option>
                ))}
              </select>
              <p className="indice">
                Un mail par fournisseur. Les {mails.length} partent ensemble, ou aucun.
              </p>
            </div>
          )}

          {/* Le motif de la relecture, avant le mail : c'est lui qui explique
              pourquoi cette fenêtre s'ouvre. */}
          {mail.manquants.length > 0 && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }}
            >
              <h3 style={{ color: 'var(--ocre)' }}>
                {mail.manquants.length} produit{mail.manquants.length > 1 ? 's' : ''} absent
                {mail.manquants.length > 1 ? 's' : ''} du mail
              </h3>
              <p style={{ fontSize: 12.5, color: 'var(--ocre)', margin: 0 }}>
                Ces références n&apos;ont pas de correspondance au référentiel :{' '}
                <span className="mono">{mail.manquants.join(', ')}</span>. Elles ont été retirées du
                mail — {mail.fournisseur} ne reconnaîtrait pas notre désignation interne. Ajoutez-les
                à la main ci-dessous si vous savez comment il les nomme, et complétez le référentiel
                dans Réglages pour la prochaine fois.
              </p>
            </div>
          )}

          {/*
            * Le choix du destinataire, tant qu'il n'y en a pas.
            *
            * Le cas d'origine est « Écrire le mail sans les produits » : sans
            * produit reconnu, rien ne rattache la commande à une société, le
            * mail s'ouvrait sans destinataire et il fallait retaper une adresse
            * qui figure pourtant dans l'annuaire.
            *
            * La condition porte sur le DESTINATAIRE, pas sur la société, et
            * c'est ce qui en fait un filet : vider le champ ramène la liste.
            * Se tromper de ligne dans un menu déroulant est facile, et le
            * mauvais fournisseur ne se voyait qu'au libellé de l'onglet — une
            * fois le choix fait, il n'existait aucun moyen d'en changer.
            *
            * Elle couvre du même coup la société connue mais absente de
            * l'annuaire : là aussi le champ est vide, et l'annuaire contient
            * peut-être la même maison sous un autre nom.
            */}
          {sansDestinataire && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }}
            >
              <h3 style={{ color: 'var(--ocre)' }}>À qui cette commande part-elle ?</h3>
              <p style={{ fontSize: 12.5, color: 'var(--ocre)', marginBottom: 10 }}>
                {mail.societe
                  ? <>Le mail est écrit pour <strong>{mail.societe}</strong>, sans adresse.
                      Choisissez le fournisseur pour la remplir depuis l&apos;annuaire — reprenez
                      la liste si ce n&apos;est pas la bonne maison.</>
                  : <>Aucun produit reconnu, donc aucun fournisseur déduit. Choisissez-le pour
                      remplir les adresses depuis l&apos;annuaire.</>}
                {' '}Le mail est réécrit dans sa langue. Vous pouvez aussi saisir une adresse à la
                main plus bas.
              </p>
              <select
                aria-label="Fournisseur destinataire"
                value=""
                disabled={!societes.length}
                onChange={(e) => { if (e.target.value) designerFournisseur(e.target.value); }}
              >
                <option value="">
                  {societes.length ? 'Choisir un fournisseur…' : 'Annuaire vide — voir Réglages'}
                </option>
                {societes.map((nom) => <option key={nom} value={nom}>{nom}</option>)}
              </select>
            </div>
          )}

          {mail.societe && !mail.contactConnu && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--rouge)', background: 'var(--brick-soft)' }}
            >
              <h3 style={{ color: 'var(--brick)' }}>{mail.fournisseur} n&apos;est pas à l&apos;annuaire</h3>
              <p style={{ fontSize: 12.5, color: 'var(--brick)', margin: 0 }}>
                Aucune adresse n&apos;a été devinée. Saisissez-la ci-dessous, et ajoutez-la dans
                Réglages pour ne plus avoir à le faire. Le mail est écrit en français par défaut.
              </p>
            </div>
          )}

          <div className="field">
            <label htmlFor="cf-to">
              Destinataires
              <span className="tag" style={{ marginLeft: 8 }}>{LIBELLE_LANGUE[mail.langue]}</span>
            </label>
            <input
              id="cf-to" type="text" value={mail.destinataire}
              placeholder="adresses séparées par des virgules"
              onChange={(e) => modifier('destinataire', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="cf-obj">Objet</label>
            <input id="cf-obj" value={mail.objet} onChange={(e) => modifier('objet', e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="cf-corps">Message</label>
            <textarea
              id="cf-corps" rows={18} value={mail.message}
              onChange={(e) => modifier('message', e.target.value)}
            />
            <p className="indice">
              Les lignes commençant par « - » deviennent une liste à puces dans le mail.
            </p>
          </div>

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Le mail <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ :
            relisez-le ici, il n&apos;y a pas d&apos;autre relecture. La commande n&apos;est
            marquée transmise que si tous les mails sont bien partis.
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="button" className="btn pri" disabled={envoi.isPending}
            onClick={() => void deposer()}
          >
            {envoi.isPending
              ? <><span className="spin" /> Envoi…</>
              : `Envoyer ${mails.length > 1 ? `les ${mails.length} commandes` : 'la commande'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compose un mail par fournisseur de la commande.
 *
 * Même fonction que côté serveur — le front n'a pas sa propre version du mail,
 * il montre celle que l'API enverrait, puis renvoie ce qui a été corrigé.
 */
function composerTous(
  commande: CommandeListee,
  reglages: DocumentReglages | undefined,
): MailRelu[] {
  const groupes = new Map<string, CommandeListee['articles']>();
  for (const a of commande.articles) {
    const societe = (a.fournisseur ?? '').trim();
    if (!societe) continue;
    if (!groupes.has(societe)) groupes.set(societe, []);
    groupes.get(societe)!.push(a);
  }

  // Aucune ligne rattachée à un fournisseur — commande vide, ou références
  // toutes inconnues. On compose quand même UN mail, sans destinataire et avec
  // ses lignes « à compléter » : refuser laisserait l'utilisateur sans aucun
  // moyen d'avancer, ce qui est exactement le mur qu'on vient de retirer.
  if (!groupes.size) groupes.set('', commande.articles);

  return Array.from(groupes, ([societe, lignes]) => composerUn(commande, reglages, societe, lignes));
}

/**
 * Compose le mail d'UNE société, à partir de lignes déjà groupées.
 *
 * Extrait de `composerTous` pour être rejoué lorsqu'on désigne le fournisseur à
 * la main : adresses, langue, objet et corps en dépendent tous. Recomposer est
 * la seule façon honnête de changer de destinataire — se contenter de remplacer
 * les adresses laisserait un mail rédigé dans la langue du précédent.
 */
export function composerUn(
  commande: CommandeListee,
  reglages: DocumentReglages | undefined,
  societe: string,
  lignes: CommandeListee['articles'],
): MailRelu {
  const m = composerCommandeFournisseur(reglages ?? REGLAGES_PAR_DEFAUT, {
    ref: commande.ref,
    societe,
    // Même critère que l'opération envoyée à l'API, et pour la même raison :
    // une commande déjà transmise ne se represente pas comme une nouveauté.
    repasse: Boolean(commande.dateEnvoiFournisseur),
    client: commande.client,
    entreprise: commande.entreprise,
    telephone: commande.telephone,
    adresse: commande.adresse,
    articles: lignes.map((a) => ({
      reference: a.reference,
      correspondance: a.correspondance,
      nom: a.nom,
      unite: a.unite,
      quantite: a.quantite,
    })),
  });
  return {
    societe,
    fournisseur: societe || '(fournisseur à préciser)',
    lignes,
    destinataire: m.destinataires,
    objet: m.objet,
    message: m.corps,
    langue: m.langue,
    manquants: m.manquants,
    sansProduit: m.sansProduit,
    contactConnu: m.contactConnu,
  };
}

/**
 * Ce qui manque et se répare en corrigeant les ARTICLES, dit en une phrase —
 * `null` quand rien ne manque de ce côté.
 *
 * Le carrefour ne se propose que là : ailleurs, corriger les articles n'y
 * changerait rien, et la fenêtre de relecture s'ouvre directement.
 */
export function motifArticles(
  commande: CommandeListee,
  reglages: DocumentReglages | undefined,
): string | null {
  const mails = composerTous(commande, reglages);

  if (mails.some((m) => m.sansProduit)) {
    return `${commande.ref} ne porte aucune ligne produit : il n'y a rien à commander, et aucun `
      + 'fournisseur à qui écrire.';
  }

  const manquants = Array.from(new Set(mails.flatMap((m) => m.manquants)));
  if (!manquants.length) return null;

  const total = commande.articles.length;
  const trouvees = total - manquants.length;
  const compte = trouvees === 0
    ? `Aucune des ${total} référence${total > 1 ? 's' : ''} de ${commande.ref} n'a de correspondance `
      + 'au référentiel'
    : `${manquants.length} référence${manquants.length > 1 ? 's' : ''} sur ${total} n'${
      manquants.length > 1 ? 'ont' : 'a'} pas de correspondance au référentiel`;

  return `${compte} : ${manquants.join(', ')}. Sans elle, le fournisseur ne peut pas savoir ce `
    + "qu'on lui commande — notre désignation interne ne lui dit rien.";
}
